import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit3, Trash2, Phone, Mail, Building2, User as UserIcon, RefreshCw, X } from 'lucide-react';
import partyService from '../../../services/partyService';
import toast from 'react-hot-toast';

const fmt = (n) => (n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

function PartyFormModal({ party, onSave, onClose }) {
  const isEdit = Boolean(party?._id);
  const [form, setForm] = useState(party || {
    type: 'customer', name: '', gstin: '', mobile: '', email: '',
    state: 'Tamil Nadu', stateCode: '33', placeOfSupply: '33-Tamil Nadu',
    gstRegType: 'regular', creditLimit: 0, creditDays: 0, paymentTerms: 'Immediate',
    billingAddress: { line1: '', city: '', state: 'Tamil Nadu', pincode: '' }, notes: '', isActive: true
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required');
    setLoading(true);
    try {
      const res = isEdit ? await partyService.update(party._id, form) : await partyService.create(form);
      toast.success(isEdit ? 'Party updated' : 'Party created');
      onSave(res.data);
    } catch (err) { toast.error(err.response?.data?.message || err.message); }
    finally { setLoading(false); }
  };

  const F = ({ label, field, type = 'text', placeholder = '' }) => (
    <div>
      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">{label}</label>
      <input type={type} value={form[field] || ''} placeholder={placeholder}
        onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
        className="w-full px-3 py-2 text-xs font-semibold rounded-xl border border-slate-200 bg-slate-50 outline-none focus:border-purple-400" />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-2xl my-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="font-bold text-base text-slate-900">{isEdit ? 'Edit Party' : 'Add New Party'}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Type & Name */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Type</label>
              <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                className="w-full px-3 py-2 text-xs font-semibold rounded-xl border border-slate-200 bg-slate-50">
                <option value="customer">Customer</option>
                <option value="supplier">Supplier</option>
                <option value="both">Both</option>
              </select>
            </div>
            <div className="sm:col-span-2"><F label="Party Name *" field="name" placeholder="Full legal name" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <F label="GSTIN" field="gstin" placeholder="33AAAPZ1234A1Z1" />
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">GST Reg. Type</label>
              <select value={form.gstRegType || 'regular'} onChange={e => setForm(f => ({ ...f, gstRegType: e.target.value }))}
                className="w-full px-3 py-2 text-xs font-semibold rounded-xl border border-slate-200 bg-slate-50">
                <option value="regular">Regular</option>
                <option value="composition">Composition</option>
                <option value="unregistered">Unregistered</option>
                <option value="consumer">Consumer</option>
                <option value="sez">SEZ</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <F label="Mobile" field="mobile" placeholder="+91 9876543210" />
            <F label="Email" field="email" type="email" placeholder="party@example.com" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <F label="State" field="state" placeholder="Tamil Nadu" />
            <F label="Place of Supply" field="placeOfSupply" placeholder="33-Tamil Nadu" />
            <F label="PAN" field="pan" placeholder="AAAPZ1234A" />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Billing Address</label>
            <input value={form.billingAddress?.line1 || ''} placeholder="Street / Door No"
              onChange={e => setForm(f => ({ ...f, billingAddress: { ...f.billingAddress, line1: e.target.value } }))}
              className="w-full px-3 py-2 text-xs font-semibold rounded-xl border border-slate-200 bg-slate-50 outline-none focus:border-purple-400 mb-2" />
            <div className="grid grid-cols-3 gap-2">
              <input value={form.billingAddress?.city || ''} placeholder="City"
                onChange={e => setForm(f => ({ ...f, billingAddress: { ...f.billingAddress, city: e.target.value } }))}
                className="px-3 py-2 text-xs font-semibold rounded-xl border border-slate-200 bg-slate-50 outline-none" />
              <input value={form.billingAddress?.state || ''} placeholder="State"
                onChange={e => setForm(f => ({ ...f, billingAddress: { ...f.billingAddress, state: e.target.value } }))}
                className="px-3 py-2 text-xs font-semibold rounded-xl border border-slate-200 bg-slate-50 outline-none" />
              <input value={form.billingAddress?.pincode || ''} placeholder="Pincode"
                onChange={e => setForm(f => ({ ...f, billingAddress: { ...f.billingAddress, pincode: e.target.value } }))}
                className="px-3 py-2 text-xs font-semibold rounded-xl border border-slate-200 bg-slate-50 outline-none" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Credit Limit (₹)</label>
              <input type="number" min="0" value={form.creditLimit || 0} onChange={e => setForm(f => ({ ...f, creditLimit: e.target.value }))}
                className="w-full px-3 py-2 text-xs font-mono font-semibold rounded-xl border border-slate-200 bg-slate-50 outline-none" />
            </div>
            <div><label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Credit Days</label>
              <input type="number" min="0" value={form.creditDays || 0} onChange={e => setForm(f => ({ ...f, creditDays: e.target.value }))}
                className="w-full px-3 py-2 text-xs font-mono font-semibold rounded-xl border border-slate-200 bg-slate-50 outline-none" />
            </div>
            <div><label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Payment Terms</label>
              <input value={form.paymentTerms || 'Immediate'} onChange={e => setForm(f => ({ ...f, paymentTerms: e.target.value }))}
                className="w-full px-3 py-2 text-xs font-semibold rounded-xl border border-slate-200 bg-slate-50 outline-none" />
            </div>
          </div>
          <div><F label="Notes" field="notes" placeholder="Internal notes" /></div>
        </form>
        <div className="flex justify-end gap-2 px-6 pb-5">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-bold rounded-xl border border-slate-200 text-slate-600">Cancel</button>
          <button onClick={handleSubmit} disabled={loading}
            className="px-6 py-2 text-xs font-bold rounded-xl bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-60">
            {loading ? 'Saving…' : (isEdit ? 'Update Party' : 'Add Party')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Parties() {
  const navigate = useNavigate();
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ type: 'all', search: '' });
  const [modal, setModal] = useState(null); // null | 'new' | party object (edit)

  const fetchParties = async () => {
    setLoading(true);
    try {
      const res = await partyService.getAll({ type: filter.type === 'all' ? undefined : filter.type, search: filter.search || undefined });
      setParties(res.data || []);
    } catch { toast.error('Failed to load parties'); }
    finally { setLoading(false); }
  };

  useEffect(() => { const t = setTimeout(fetchParties, 300); return () => clearTimeout(t); }, [filter.type, filter.search]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this party? This cannot be undone.')) return;
    try { await partyService.delete(id); toast.success('Deleted'); fetchParties(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed to delete'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-slate-900">Parties</h1>
          <p className="text-xs text-slate-500 font-medium">{parties.length} customers & suppliers</p>
        </div>
        <button onClick={() => setModal('new')}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs shadow-sm">
          <Plus className="w-4 h-4" /> Add Party
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {/* Filters */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
          <Search className="w-4 h-4 text-slate-400 shrink-0" />
          <input type="text" placeholder="Search name, GSTIN, mobile…" value={filter.search}
            onChange={e => setFilter(f => ({ ...f, search: e.target.value }))}
            className="flex-1 text-sm font-medium text-slate-900 placeholder-slate-400 outline-none bg-transparent" />
          <select value={filter.type} onChange={e => setFilter(f => ({ ...f, type: e.target.value }))}
            className="text-xs font-bold px-3 py-1.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-700">
            <option value="all">All Types</option>
            <option value="customer">Customers</option>
            <option value="supplier">Suppliers</option>
            <option value="both">Both</option>
          </select>
          <button onClick={fetchParties} className="p-2 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="px-4 py-2.5 text-left font-bold text-slate-500 uppercase">Name</th>
                <th className="px-4 py-2.5 text-left font-bold text-slate-500 uppercase">GSTIN</th>
                <th className="px-4 py-2.5 text-left font-bold text-slate-500 uppercase hidden sm:table-cell">Contact</th>
                <th className="px-4 py-2.5 text-center font-bold text-slate-500 uppercase">Type</th>
                <th className="px-4 py-2.5 text-right font-bold text-slate-500 uppercase hidden md:table-cell">Outstanding</th>
                <th className="px-4 py-2.5 text-center font-bold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className="px-4 py-3"><div className="h-3 bg-slate-100 rounded-full" /></td>
                    ))}
                  </tr>
                ))
              ) : parties.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400 font-semibold">
                  No parties found. <button onClick={() => setModal('new')} className="text-purple-600 underline">Add one?</button>
                </td></tr>
              ) : parties.map(p => (
                <tr key={p._id} className="hover:bg-slate-50/70 group">
                  <td className="px-4 py-3">
                    <p className="font-bold text-slate-900">{p.name}</p>
                    {p.state && <p className="text-slate-400 text-[10px]">{p.state}</p>}
                  </td>
                  <td className="px-4 py-3 font-mono font-bold text-slate-700">{p.gstin || <span className="text-slate-300 italic">Unregistered</span>}</td>
                  <td className="px-4 py-3 hidden sm:table-cell text-slate-600">
                    <div className="space-y-0.5">
                      {p.mobile && <p className="flex items-center gap-1"><Phone className="w-3 h-3 text-slate-400" />{p.mobile}</p>}
                      {p.email && <p className="flex items-center gap-1 truncate max-w-[150px]"><Mail className="w-3 h-3 text-slate-400" />{p.email}</p>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase
                      ${p.type === 'customer' ? 'bg-emerald-100 text-emerald-700' :
                        p.type === 'supplier' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                      {p.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-bold hidden md:table-cell"
                    style={{ color: (p.currentOutstanding || 0) > 0 ? '#dc2626' : '#16a34a' }}>
                    ₹{fmt(p.currentOutstanding)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => setModal(p)}
                        className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-500" title="Edit">
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDelete(p._id)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-red-400" title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <PartyFormModal
          party={modal === 'new' ? null : modal}
          onSave={() => { setModal(null); fetchParties(); }}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
